import type { CompanyGraph } from "@/lib/company-graph";

// A DETERMINISTIC RADIAL LAYOUT — the same picture on every load, because a force simulation
// rearranges itself between visits and a reader comparing two searches would be comparing two
// different drawings. Things sit on an outer ring grouped by what they are; each company sits on an
// inner ring at the circular mean of the things it touches, which is the standard one-pass crossing
// heuristic. No iteration, no seed, no settling.

/**
 * The ring's radii at the size it was drawn for, and the room one label needs ON the ring.
 *
 * THE CIRCUMFERENCE IS THE BUDGET. These were constants, so thirty-one things and seventeen
 * hundred got the same 2 538 px of circle: at 1.5 px per label the outer ring went solid black.
 * A ring grows outwards, which is the one direction it can grow, so the radii follow the counts
 * and the floors keep a small graph looking exactly as it did.
 */
const R_THING = 404;
const R_COMPANY = 243;

/**
 * Tangential room per label where the labels are closest together — at the node itself.
 *
 * They radiate, so they only diverge from there; this is the binding constraint and the rest is
 * free. A little more than a line height, so neighbours have air rather than touching.
 */
const RING_PITCH = 16;

/** The ring that fits [count] labels at [RING_PITCH], never tighter than [floor]. */
const ringFor = (count: number, floor: number) => Math.max(floor, (count * RING_PITCH) / (2 * Math.PI));

/**
 * Roughly how wide a label is, per character, at the size they are drawn.
 *
 * A measurement would need a DOM; this only has to be close enough to decide how much room the
 * ring needs around it, and erring long costs blank margin while erring short clips a name.
 */
export const PER_CHAR = 6;
const MARGIN = 24;

/** How far from its own ring or row a label starts. */
export const LABEL_GAP = 11;

/**
 * Lane layouts: the pitch between neighbours on the line, and the lanes' distance from it.
 *
 * A column layout packs tighter because its labels lie flat and need only their own line height;
 * a row layout stands them on end and needs room for the letters side by side.
 */
const COLUMN = 26;
const ROW = 18;
const BAND = 220;

/** The drawn height of a group's named box — see `CompanyGraphPage`, which draws it. */
const CLUSTER_HEIGHT = 20;

/**
 * For each DRAWN node, the opened groups around it, outermost first.
 *
 * Handed in rather than read off `graph.clusters`, because only the collapse knows which groups
 * are open and which of their members are on the picture — and a layout that guessed from the
 * clusters alone could only ever see one level of it.
 */
export type Enclosing = ReadonlyMap<string, string[]>;

/** A company name longer than this is cut — its labels point INWARD, across the chord bundle. */
export const COMPANY_LABEL = 26;
const GAP = 0.055; // a wedge of blank between groups, so the outer ring reads as grouped

/**
 * The outer ring's groups.
 *
 * Authored, not derived: the graph says what a thing IS only by the edges pointing at it, and
 * grouping by that would put every clinical speciality in one lump with the commodities. These are
 * the four kinds a reader actually distinguishes. A node named in none of them lands in "other",
 * so a node added to the fixture appears rather than disappearing.
 */
export const THEMES: { key: string; of: string[] }[] = [
  {
    key: "care",
    of: [
      "Klinik", "Notfallmedizin", "Intensivmedizin", "Geburtshilfe", "Kardiologie", "Onkologie",
      "Radiologie", "Psychiatrie", "Neurologie", "Palliativmedizin", "Geriatrie", "Altersmedizin",
      "Rehabilitation", "Hospiz", "Pflege", "Krankenpflege", "Seelsorge", "Medizin",
      "Kinder- und Jugendhilfe", "Schwangerschaftsabbruch",
    ],
  },
  { key: "bought in", of: ["Blutprodukte", "Laborleistungen", "Arzneimittel", "Medizintechnik", "Hilfsmittel"] },
  { key: "trains", of: ["Pflegefachkraft", "Fachinformatiker"] },
  { key: "other", of: ["Familien", "AI", "Biotech", "Pharma"] },
];

export interface PlacedNode {
  id: string;
  /**
   * `cluster` is a group of things folded into one node by `collapse`.
   *
   * The layouts treat it exactly as a thing — same ring, same lane, same label — because it IS one
   * as far as placing goes. Only the view needs to tell them apart, to mark it and to know what
   * opens.
   */
  kind: "thing" | "company" | "cluster";
  x: number;
  y: number;
  /** The angle it sits at; 0 for a layout that has no angles. */
  a: number;
  degree: number;
  /** A company with no edge at all — drawn hollow, because it is still in the universe. */
  edgeless: boolean;
  /**
   * How the label is turned and which way it runs from the node.
   *
   * Decided here rather than by the view: a layout that puts things on a ring and one that puts
   * them in rows want different label angles, and a view branching on which layout it was handed
   * would be the layout choice made twice, in two places, able to disagree.
   */
  labelTransform: string;
  labelAnchor: "start" | "end";
  /**
   * Where this node's label ENDS, in canvas coordinates.
   *
   * The label is the widest part of a node by a long way, and anything drawn around nodes — the
   * box round an opened group — has to enclose it or it encloses dots and crosses out names. Only
   * the layout knows which way a label runs, so only the layout can say where it stops.
   */
  labelEnd: { x: number; y: number };
}

export interface PlacedEdge {
  id: string;
  from: PlacedNode;
  to: PlacedNode;
  predicate: string;
  status?: string | null;
  evidence?: string;
  /** A company-to-company supply arrow: supply is ternary and the customer is not the object. */
  ternary: boolean;
  /** The path to draw, for the same reason the label transform lives here. */
  d: string;
  /** How many member edges this one stands for; 1 for an ordinary edge. */
  weight: number;
}

export interface PlacedArc {
  key: string;
  a0: number;
  a1: number;
}

/**
 * How a graph is laid out.
 *
 * `band` and `columns` are the same arrangement turned ninety degrees — companies on one line with
 * the things in two lanes either side of it — and which one reads better is a property of the
 * screen, not of the graph: a wide window takes rows, a tall one takes columns. `radial` is the
 * ring, which cannot grow in either direction.
 */
export type LayoutKind = "radial" | "band" | "columns";

/**
 * Which side of the picture is placed by name, and which way round.
 *
 * THE LAYOUT IS ALREADY A CASCADE: one side is placed, and the other follows it, each node sitting
 * at the average position of whatever it touches. So an ordering is not a separate pass — it is a
 * choice of which side leads. Name the companies and the things arrange themselves around them;
 * name the things and the companies do.
 */
export type Order = "relations" | "companies" | "companies-desc" | "things" | "things-desc";

/**
 * Offered in this order, and the first is what a reader gets before they choose.
 *
 * `relations` names neither side: each layout keeps the cascade it was built on, which is not the
 * same one in both — the lanes start from the companies in the alphabet, the ring starts from the
 * things and puts each company at the circular mean of what it touches, because that is what keeps
 * a ring's chords from crossing. Making "companies A to Z" the default would quietly take that
 * away.
 */
export const ORDERS: Order[] = ["relations", "companies", "companies-desc", "things", "things-desc"];

/** True when the THINGS are the side placed by name and the companies follow. */
const thingsLead = (order: Order) => order === "things" || order === "things-desc";

/** The direction of the name comparison, as a multiplier. */
const way = (order: Order) => (order === "companies-desc" || order === "things-desc" ? -1 : 1);

/** Offered in this order, and the first is what a reader gets before they choose. */
export const LAYOUTS: LayoutKind[] = ["columns", "band", "radial"];

export interface Layout {
  kind: LayoutKind;
  nodes: PlacedNode[];
  byId: Map<string, PlacedNode>;
  edges: PlacedEdge[];
  arcs: PlacedArc[];
  /** SIZED TO ITS OWN LABELS. A fixed box clips the long ones, and the corpus decides how long. */
  width: number;
  height: number;
  cx: number;
  cy: number;
}

/** Circular mean, so a company between 350° and 10° lands at 0° rather than at 180°. */
function meanAngle(angles: number[]): number {
  if (angles.length === 0) return Math.PI / 2;
  const x = angles.reduce((sum, a) => sum + Math.cos(a), 0) / angles.length;
  const y = angles.reduce((sum, a) => sum + Math.sin(a), 0) / angles.length;
  return Math.atan2(y, x);
}

/**
 * The layout a reader picked.
 *
 * A RING STOPS WORKING SOMEWHERE AROUND A HUNDRED COMPANIES: the inner circle has as many labels as
 * it has companies, each pointing inward across the chord bundle, and no amount of haloing makes a
 * hundred of those readable. `band` trades the shape for room — rows that run as far sideways as
 * they need to and scroll — which is the thing a ring can never do.
 */
export function layout(
  graph: CompanyGraph,
  kind: LayoutKind,
  enclosing: Enclosing = new Map(),
  order: Order = "relations",
): Layout {
  if (kind === "band") return lanes(graph, "band", enclosing, order);
  if (kind === "columns") return lanes(graph, "columns", enclosing, order);
  return radial(graph, enclosing, order);
}

function radial(graph: CompanyGraph, enclosing: Enclosing, order: Order): Layout {
  const labels = graph.nodes.map((node) => node.label);
  const isCluster = kindOf(graph);
  // The box is as wide as the ring plus the longest thing a label can reach past it. Measured on
  // the real corpus, a name like "CCS-Projekt zur CO2-Abscheidung am Mittelkalorikkraftwerk" runs
  // 250px beyond the ring and was simply cut off by a box fixed at 1120.
  const longest = labels.reduce((most, label) => Math.max(most, label.length), 0);
  // THE INNER RING KEEPS ITS SHARE OF THE OUTER ONE. Sized from its own count alone it stayed
  // small while the outer one grew, so a hundred and fifty companies sat in a knot at the middle
  // and every chord in the picture converged on it. The proportion is the one the ring was drawn
  // with (243 of 404), which leaves a small graph exactly where it was and gives a large one an
  // inner circle as big as the picture can afford.
  const wanted = ringFor(labels.length, R_THING);
  const rCompany = Math.max(ringFor(graph.companies.length, R_COMPANY), (wanted * R_COMPANY) / R_THING);
  // THE SPACE BETWEEN THE RINGS IS WHAT THE CHORDS CROSS, so the outer one keeps at least the gap
  // it was drawn with — a company ring that grew past it would leave the things nowhere to go.
  const rThing = Math.max(wanted, rCompany + (R_THING - R_COMPANY));
  const reach = rThing + LABEL_GAP + longest * PER_CHAR + MARGIN;
  const size = Math.max(2 * reach, 2 * (rThing + 120));
  const cx = size / 2;
  const cy = size / 2;
  // AN OPENED GROUP IS ITS OWN WEDGE. The ring places things by theme, so without this a group's
  // members land wherever their themes put them and the box drawn round them spans an arc holding
  // everything in between — measured on the real corpus, up to sixty things that are not in it.
  // As a wedge they are contiguous, with the ring's own gap either side, and the box is the group.
  const opened = new Map<string, string[]>();
  for (const [id, around] of enclosing) {
    const outermost = around[0];
    if (outermost !== undefined) opened.set(outermost, [...(opened.get(outermost) ?? []), id]);
  }
  const inAGroup = new Set([...opened.values()].flat());

  const grouped = THEMES.map((theme) => ({
    ...theme,
    of: theme.of.filter((id) => labels.includes(id) && !inAGroup.has(id)),
  }));
  const placedThings = new Set([...grouped.flatMap((t) => t.of), ...inAGroup]);
  const leftovers = labels.filter((id) => !placedThings.has(id));
  const themed = leftovers.length
    ? grouped.map((t) => (t.key === "other" ? { ...t, of: [...t.of, ...leftovers] } : t))
    : grouped;
  // The opened groups first and in a fixed order, so the ring is the same drawing every time.
  const byName = (a: string, b: string) => way(order) * a.localeCompare(b);
  const groups = [
    ...[...opened.keys()].sort(byName).map((key) => ({ key, of: opened.get(key)!.slice().sort(byName) })),
    // THE THINGS LEAD, SO THEY ARE NAMED. The wedges stay — a group's members are drawn inside a
    // box and scattering them around the ring would draw it around everything — but within each
    // one, and within the themes, the order is the reader's.
    ...(thingsLead(order) ? themed.map((it) => ({ ...it, of: [...it.of].sort(byName) })) : themed),
  ];

  // Each group gets arc proportional to how many things it holds, plus a fixed gap after it.
  const total = groups.reduce((sum, t) => sum + t.of.length, 0) || 1;
  const span = 2 * Math.PI - GAP * groups.length;
  const thingAngle = new Map<string, number>();
  const arcs: PlacedArc[] = [];
  let at = -Math.PI / 2 + GAP / 2;
  for (const theme of groups) {
    const step = theme.of.length ? (span * (theme.of.length / total)) / theme.of.length : 0;
    const a0 = at;
    for (const id of theme.of) {
      thingAngle.set(id, at + step / 2);
      at += step;
    }
    arcs.push({ key: theme.key, a0, a1: at });
    at += GAP;
  }

  // Each company at the circular mean of the things it touches; one with none is parked where it
  // falls in the alphabet, because there is nothing about it to put it anywhere else.
  //
  // EACH THING COUNTED ONCE. Collected per edge, a company related to one thing under three
  // relations was pulled three times toward it — and the same company's pull changed when a group
  // it reaches was folded, because folding rolls its edges into one.
  const touched = new Map<string, Set<string>>(graph.companies.map((c) => [c, new Set<string>()]));
  for (const edge of graph.edges) {
    if (thingAngle.has(edge.object)) touched.get(edge.company)?.add(edge.object);
  }
  const meanFor = (company: string) =>
    meanAngle([...(touched.get(company) ?? [])].map((it) => thingAngle.get(it)!));
  // NAMED, OR FOLLOWING. Asked for by name the companies take that order outright; otherwise each
  // sits at the circular mean of the things it touches, which is the cascade the ring is built on.
  const ordered = thingsLead(order) || order === "relations"
    ? [...graph.companies].sort((a, b) => {
        // A TOLERANCE, because these are means of angles: two companies that belong at the same
        // place come out a few bits apart and the sign of the difference is then noise, which
        // decides the order and moves things for no reason a reader can see.
        const diff = meanFor(a) - meanFor(b);
        return Math.abs(diff) > 1e-9 ? diff : a.localeCompare(b);
      })
    : [...graph.companies].sort(byName);
  const companyAngle = new Map(
    ordered.map((id, index) => [id, -Math.PI / 2 + ((index + 0.5) * (2 * Math.PI)) / ordered.length]),
  );

  const degree = new Map<string, number>();
  const bump = (id: string) => degree.set(id, (degree.get(id) ?? 0) + 1);
  for (const edge of graph.edges) {
    bump(edge.company);
    bump(edge.object);
  }

  const node = (id: string, kind: "thing" | "company" | "cluster", a: number, r: number): PlacedNode => {
    // A label on the left half is upside down unless it is turned again, and it then runs the
    // other way, so its anchor flips with it.
    const flip = Math.cos(a) < 0;
    const gap = kind === "thing" ? LABEL_GAP : -LABEL_GAP;
    return {
      id,
      kind,
      a,
      x: cx + Math.cos(a) * r,
      y: cy + Math.sin(a) * r,
      degree: degree.get(id) ?? 0,
      edgeless: kind === "company" && (degree.get(id) ?? 0) === 0,
      labelTransform: `rotate(${(a * 180) / Math.PI}) translate(${gap},0)${flip ? " rotate(180)" : ""}`,
      labelAnchor: kind === "thing" ? (flip ? "end" : "start") : flip ? "start" : "end",
      // Along the ray, outward for a thing and inward for a company — the same direction `gap`
      // carries, just as far as the text actually reaches.
      labelEnd: {
        x: cx + Math.cos(a) * (r + (kind === "thing" ? 1 : -1) * (LABEL_GAP + labelWidth(id, kind))),
        y: cy + Math.sin(a) * (r + (kind === "thing" ? 1 : -1) * (LABEL_GAP + labelWidth(id, kind))),
      },
    };
  };

  // BUILT FROM THE ANGLES, not from the labels with a fallback. A `?? 0` here placed a node the
  // groups failed to name on top of whatever sat at that angle — present in the data, invisible on
  // the ring, and indistinguishable from having been dropped. Every label reaches a group above, so
  // a label missing here is a bug in the grouping and shows up as a missing node rather than as a
  // collision nobody can see.
  const nodes: PlacedNode[] = [
    ...[...thingAngle].map(([id, a]) => node(id, isCluster(id) ? "cluster" : "thing", a, rThing)),
    ...[...companyAngle].map(([id, a]) => node(id, "company", a, rCompany)),
  ];
  const byId = new Map(nodes.map((n) => [n.id, n]));

  const edges = edgesOf(graph, byId);

  // A GROUP WITH NOTHING IN IT HAS NO LABEL TO SHOW. The groups are authored around one corpus;
  // on another, three of the four are empty and their names pile up at the same angle, which is
  // how the ring came to read "CBOUGHTRAINS".
  return {
    kind: "radial",
    nodes,
    byId,
    edges: edges.map((edge) => ({ ...edge, d: bow(edge, cx, cy) })),
    // A GROUP WITH NOTHING IN IT HAS NO LABEL TO SHOW. The groups are authored around one corpus;
    // on another, three of the four are empty and their names pile up at the same angle, which is
    // how the ring came to read "CBOUGHTRAINS".
    arcs: arcs.filter((it) => it.a1 > it.a0),
    width: size,
    height: size,
    cx,
    cy,
  };
}

/**
 * Companies on one line, things in two lanes either side of it, growing along that line.
 *
 * TWO LANES AND NOT ONE. Half as many labels per lane at the same length, and every edge is short
 * — a company reaches the lane above it or the lane below it, never across the whole picture. The
 * ring's chords all pass through one middle, which is what makes a dense one unreadable; here
 * there is no middle to pass through.
 *
 * ROWS AND COLUMNS ARE THE SAME DRAWING TURNED NINETY DEGREES, so they are one function with one
 * axis swapped. Written twice they would drift: the barycentre ordering, the alternation and the
 * sizing are the parts that matter and none of them is about which way round it is.
 *
 * Which reads better is a property of the SCREEN. Rows run off the side of a wide window and
 * columns down a tall one, and only the reader knows which they have — so both are offered rather
 * than one being chosen for them.
 */
function lanes(graph: CompanyGraph, kind: "band" | "columns", enclosing: Enclosing, order: Order): Layout {
  const vertical = kind === "columns";
  const labels = graph.nodes.map((node) => node.label);
  const isCluster = kindOf(graph);
  const longest = labels.reduce((most, label) => Math.max(most, label.length), 0);

  const degree = new Map<string, number>();
  const bump = (id: string) => degree.set(id, (degree.get(id) ?? 0) + 1);
  for (const edge of graph.edges) {
    bump(edge.company);
    bump(edge.object);
  }

  const byName = (a: string, b: string) => way(order) * a.localeCompare(b);
  // A COLUMN LAYOUT CAN PACK TIGHTER: its labels lie flat and need only their own line height,
  // while a row layout stands them on end and needs room for the letters side by side.
  const pitch = vertical ? ROW : COLUMN;

  // ORDERED ON THE COMPANY'S INDEX, never on where it will be drawn.
  //
  // NOT HELD BY A TEST, held by an argument: every key here is a mean over a set of integers, that
  // set is the same whether a group is folded or open, so every key is bit-for-bit the same number
  // in both states and the sort cannot disagree with itself. A small fixture cannot show the
  // difference — the two rankings are mathematically identical and differ only in rounding — so
  // the evidence is the real corpus: 55 groups in `first20`, two of which changed lanes before
  // this and none after, in both lane layouts. The length is not known yet —
  // it depends on how many slots the lanes need, which depends on this ordering — and a
  // provisional one would have to be scaled away again later. Worse, it would not quite scale
  // away: two things whose averages are equal compute to the same double through one scale and to
  // doubles a bit apart through another, so opening a group (which lengthens the picture) broke
  // ties differently and shuffled a few things by one place. One place is one run, and a lane is
  // the parity of a run's index. Indices are the same integers in both states, so every key is
  // bit-for-bit identical and the question cannot arise.

  // Each thing beside the average position of the companies that reach it — the same one-pass
  // crossing heuristic the ring uses, in the one dimension this layout has.
  //
  // EACH COMPANY COUNTED ONCE. Counted per edge, a company that offers four of a group's things
  // pulls that group four times while it is open and once while it is folded, because folding
  // rolls its four edges into one. The group's rank therefore moved when it was opened, and since
  // a lane is the parity of a run's index, crossing a single neighbour sent it to the other side
  // of the picture. Counted once, the two states are the same number by construction.
  const reaching = new Map<string, Set<string>>(labels.map((label) => [label, new Set<string>()]));
  const reached = new Map<string, Set<string>>(graph.companies.map((id) => [id, new Set<string>()]));
  for (const edge of graph.edges) {
    reaching.get(edge.object)?.add(edge.company);
    reached.get(edge.company)?.add(edge.object);
  }

  /** The average index of a set of neighbours, or the middle for something with none. */
  const average = (names: Iterable<string>, placeOf: Map<string, number>, count: number) => {
    const xs = [...names].map((it) => placeOf.get(it)).filter((it): it is number => it !== undefined);
    return xs.length ? xs.reduce((sum, x) => sum + x, 0) / xs.length : (count - 1) / 2;
  };

  // WHERE AN OPENED GROUP SITS: over the companies reaching everything DRAWN inside it, as one
  // set. Measured over its members instead, a group whose own member is a group that is open too
  // has no drawn member to measure at all; averaged per member rather than pooled, a company that
  // reaches several of them counts once per member and the same drift comes back a level up.
  const within = new Map<string, string[]>();
  for (const [id, around] of enclosing) {
    for (const group of around) within.set(group, [...(within.get(group) ?? []), id]);
  }

  // WHICH SIDE LEADS. Placed by name, a side gets its order outright; the other one follows, each
  // of its members sitting at the average position of what it touches. That cascade is the layout
  // itself — the ordering choice is only which end of it a reader names.
  let companies: string[];
  let ranked: string[];
  if (thingsLead(order)) {
    // A GROUP MOVES AS ONE, under its own name: its members are drawn inside a box, so scattering
    // them alphabetically through the picture would draw that box around half of it.
    const under = (label: string) => enclosing.get(label)?.[0] ?? label;
    ranked = [...labels].sort((a, b) => byName(under(a), under(b)) || byName(a, b));
    const placeOf = new Map(ranked.map((id, index) => [id, index]));
    companies = [...graph.companies].sort((a, b) => {
      const diff = average(reached.get(a) ?? [], placeOf, ranked.length) -
        average(reached.get(b) ?? [], placeOf, ranked.length);
      return diff || a.localeCompare(b);
    });
  } else {
    companies = [...graph.companies].sort(byName);
    const placeOf = new Map(companies.map((id, index) => [id, index]));
    const meanOf = (names: Iterable<string>) => average(names, placeOf, companies.length);
    const groupAt = new Map<string, number>();
    for (const [group, ids] of within) {
      groupAt.set(group, meanOf(new Set(ids.flatMap((it) => [...(reaching.get(it) ?? [])]))));
    }
    ranked = rankThings(labels, enclosing, groupAt, meanOf, reaching, companies.length);
  }

  // ALTERNATING BY RUN, not by item. Both lanes still span the whole length and stay in order —
  // but a group's things go into ONE lane rather than straddling both. Straddling, the box drawn
  // round an opened group spans the full width and swallows everything between the lanes, which is
  // every node at that height. Keyed on the OUTERMOST group, so a nested open is one run too.
  const runs: { key: string | undefined; items: string[] }[] = [];
  let run: string[] = [];
  let runKey: string | undefined;
  for (const label of ranked) {
    const key = enclosing.get(label)?.[0];
    if (run.length > 0 && key !== undefined && key === runKey) {
      run.push(label);
      continue;
    }
    if (run.length > 0) runs.push({ key: runKey, items: run });
    run = [label];
    runKey = key;
  }
  if (run.length > 0) runs.push({ key: runKey, items: run });

  // HOW MUCH ROOM A MARK NEEDS, along the lane. A thing is a dot with a label on its own line; a
  // group is a NAMED BOX, twenty tall and as wide as its name. At one slot each the boxes overlap
  // one another and the labels above and below them — in columns by their height, in rows by
  // their width, which is far worse.
  const roomFor = (id: string) =>
    isCluster(id) ? Math.max(1, Math.ceil((vertical ? CLUSTER_HEIGHT : id.length * PER_CHAR + 12) / pitch)) : 1;

  // A BLANK SLOT EITHER SIDE OF AN OPENED GROUP. Its members are drawn inside a box, and a box
  // whose edge lands on the label above it reads as a box around that label too — which is how a
  // group of sixteen came to look like it held the two unrelated things nearest it.
  //
  // A MARK SITS IN THE MIDDLE OF THE ROOM IT CLAIMS. Claiming it to one side only would leave the
  // neighbour on the other side exactly as close as before, and a box twenty tall at an eighteen
  // pitch overlaps BOTH of them.
  const first: { id: string; at: number }[] = [];
  const second: { id: string; at: number }[] = [];
  let firstEnd = 0;
  let secondEnd = 0;
  runs.forEach((it, index) => {
    const even = index % 2 === 0;
    const lane = even ? first : second;
    let cursor = (even ? firstEnd : secondEnd) + (it.key === undefined ? 0 : 1);
    for (const id of it.items) {
      const room = roomFor(id);
      lane.push({ id, at: cursor + (room - 1) / 2 });
      cursor += room;
    }
    if (it.key !== undefined) cursor += 1;
    if (even) firstEnd = cursor;
    else secondEnd = cursor;
  });
  // ONE PITCH FOR BOTH LANES. Spreading each over the full length by its own count would give a
  // short lane a wide pitch, and a group pushed whole into one lane makes the lanes uneven.
  const pace = Math.max(firstEnd, secondEnd, 1);

  const along = MARGIN * 2 + Math.max(companies.length, pace) * pitch;
  const across = 2 * (BAND + LABEL_GAP + longest * PER_CHAR + MARGIN);
  const width = vertical ? across : along;
  const height = vertical ? along : across;
  const cx = width / 2;
  const cy = height / 2;
  const middle = vertical ? cx : cy;
  const spread = (index: number, count: number) => MARGIN + ((index + 0.5) * (along - 2 * MARGIN)) / count;
  const put = (main: number, cross: number) => (vertical ? { x: cross, y: main } : { x: main, y: cross });
  const mainOf = (node: { x: number; y: number }) => (vertical ? node.y : node.x);
  const lineOf = new Map(companies.map((id, index) => [id, spread(index, companies.length)]));

  // A LABEL RUNS AWAY FROM THE LINE, and which way that is depends on the turn. In rows the near
  // lane is above and its label goes up, which is the label frame's own +x after the rotation; in
  // columns the near lane is to the LEFT and its label goes left, which is -x. Same intent, mirrored.
  const outward = (side: boolean) => (vertical ? !side : side);

  const node = (id: string, nodeKind: "thing" | "company" | "cluster", main: number, cross: number, side: boolean): PlacedNode => ({
    id,
    kind: nodeKind,
    ...put(main, cross),
    a: 0,
    degree: degree.get(id) ?? 0,
    edgeless: nodeKind === "company" && (degree.get(id) ?? 0) === 0,
    // COLUMNS READ FLAT, ROWS STAND ON END. Stacked down a column every label has its own line and
    // needs no turning; packed along a row they would overlap their neighbours after about six
    // characters, so they turn — and then BOTH lanes keep the same rotation, because turning the
    // far one the other way leaves it upside down against the near one and a reader tilts their
    // head once per lane. The lane that runs the other way does it from its anchor instead.
    labelTransform: `${vertical ? "" : "rotate(-90) "}translate(${outward(side) ? LABEL_GAP : -LABEL_GAP},0)`,
    labelAnchor: outward(side) ? "start" : "end",
    // ALWAYS AWAY FROM THE LINE, along the cross axis — which is what both the transform and the
    // anchor are arranged to produce, turned or not. The near lane runs one way, the far lane the
    // other, and a company on the line runs with the near lane.
    labelEnd: put(main, cross + (side ? -1 : 1) * (LABEL_GAP + labelWidth(id, nodeKind))),
  });

  const nodes: PlacedNode[] = [
    ...companies.map((id) => node(id, "company", lineOf.get(id) ?? along / 2, middle, true)),
    ...first.map(({ id, at }) => node(id, isCluster(id) ? "cluster" : "thing", spread(at, pace), middle - BAND, true)),
    ...second.map(({ id, at }) => node(id, isCluster(id) ? "cluster" : "thing", spread(at, pace), middle + BAND, false)),
  ];
  const byId = new Map(nodes.map((n) => [n.id, n]));

  return {
    kind,
    nodes,
    byId,
    edges: edgesOf(graph, byId).map((edge) => ({ ...edge, d: hop(edge, vertical, middle, mainOf) })),
    arcs: [],
    width,
    height,
    cx,
    cy,
  };
}

/**
 * The things in barycentre order, with every group's contents together.
 *
 * THE WHOLE CHAIN, OUTERMOST FIRST. Sorted on its own position alone, a node inside two opened
 * groups is ordered by neither; sorted on its innermost group alone, the outer group's contents
 * split into two blocks and the box drawn round them spans everything in between. The path orders
 * by the outer group, then the inner one, then the node, so every box encloses a run.
 *
 * A POSITION AND THE NAME THAT BREAKS ITS TIES, at every step. Ties are the normal case here: a
 * thing reached by one company sits exactly where that company does, and so does every other thing
 * it reaches. Folded, a group ties with a thing and their names settle it; opened, its members
 * carry a longer key, and a comparator that treated the missing element as "less than everything"
 * made them lose every tie they used to win. One place in the order is one run, a lane is the
 * parity of a run's index, and the group appeared on the other side of the picture. Carrying the
 * GROUP's name at the group's step makes the two comparisons the same one.
 */
function rankThings(
  labels: string[],
  enclosing: Enclosing,
  groupAt: Map<string, number>,
  meanOf: (names: Iterable<string>) => number,
  reaching: Map<string, Set<string>>,
  companies: number,
): string[] {
  const keyOf = (label: string): [number, string][] => [
    ...(enclosing.get(label) ?? []).map((group) => [groupAt.get(group) ?? (companies - 1) / 2, group] as [number, string]),
    [meanOf(reaching.get(label) ?? []), label],
  ];
  return [...labels].sort((a, b) => {
    const left = keyOf(a);
    const right = keyOf(b);
    for (let i = 0; i < Math.min(left.length, right.length); i += 1) {
      const diff = left[i]![0] - right[i]![0];
      if (diff) return diff;
      const named = left[i]![1].localeCompare(right[i]![1]);
      if (named) return named;
    }
    return left.length - right.length;
  });
}

function hop(
  edge: PlacedEdge,
  vertical: boolean,
  middle: number,
  mainOf: (node: { x: number; y: number }) => number,
): string {
  const mid = (mainOf(edge.from) + mainOf(edge.to)) / 2;
  // BOTH ENDS ON THE LINE, for a company-to-company supply: a straight one would be invisible
  // underneath it, so it bows out into the near lane.
  const cross = edge.ternary ? middle - BAND * 0.45 : vertical ? edge.from.x : edge.from.y;
  const control = vertical ? `${cross},${mid}` : `${mid},${cross}`;
  return `M${edge.from.x},${edge.from.y}Q${control} ${edge.to.x},${edge.to.y}`;
}

/**
 * Which node joins which, in whatever layout placed them. The `d` is added afterwards.
 *
 * Shared because it is the DATA's shape, not the picture's: the same edges exist whichever way the
 * nodes were arranged, and deriving them twice is two places for the ternary rule to drift.
 */
/** Which drawn labels are groups rather than things — the only thing a layout needs to know. */
function kindOf(graph: CompanyGraph): (label: string) => boolean {
  const groups = new Set((graph.clusters ?? []).map((it) => it.label));
  return (label) => groups.has(label);
}

/**
 * Roughly how wide a label is drawn, which is all a bounding box needs.
 *
 * A company's label is shortened and then cut (see `COMPANY_LABEL`), so measuring the id would
 * claim a box far wider than the text in it.
 */
function labelWidth(id: string, kind: "thing" | "company" | "cluster"): number {
  const text = kind === "company" ? shortName(id).slice(0, COMPANY_LABEL) : id;
  return text.length * PER_CHAR;
}

function edgesOf(graph: CompanyGraph, byId: Map<string, PlacedNode>): PlacedEdge[] {
  const edges: PlacedEdge[] = [];
  graph.edges.forEach((edge, index) => {
    const from = byId.get(edge.company);
    const to = byId.get(edge.object);
    if (from && to) {
      edges.push({
        id: `e${index}`,
        from,
        to,
        predicate: edge.predicate,
        status: edge.status,
        evidence: edge.evidence,
        ternary: false,
        d: "",
        weight: edge.weight ?? 1,
      });
    }
    // SUPPLY IS TERNARY — who supplies WHAT to WHOM — so the customer gets its own arrow. Drawn as
    // well as the edge to the thing, not instead of it: the commodity is what the edge is about,
    // and a company-to-company line alone would hide which.
    const customer = edge.to ? byId.get(edge.to) : undefined;
    if (from && customer) {
      edges.push({
        id: `t${index}`,
        from,
        to: customer,
        predicate: edge.predicate,
        status: edge.status,
        evidence: edge.object,
        ternary: true,
        d: "",
        weight: 1,
      });
    }
  });
  return edges;
}

/**
 * A chord that bows toward the centre — the deeper the span, the deeper the bow.
 *
 * Straight lines across a ring all pass through the middle and become one dark knot; pulling the
 * control point toward the centre spreads them into readable arcs instead.
 */
function bow(edge: PlacedEdge, cx: number, cy: number): string {
  const pull = edge.ternary ? 0.58 : 0.34;
  const mx = (edge.from.x + edge.to.x) / 2;
  const my = (edge.from.y + edge.to.y) / 2;
  return `M${edge.from.x},${edge.from.y}Q${cx + (mx - cx) * pull},${cy + (my - cy) * pull} ${edge.to.x},${edge.to.y}`;
}

// ONE SET OF COLOURS, not a light and a dark pair: spark's palettes are user-chosen rather than
// OS-driven, so there is no media query to hang a second set on. These are mid-range values —
// readable on a white page and on a navy one — and they are DATA marks, which the conventions keep
// separate from the accent on purpose.
export const RELATION_COLOUR: Record<string, string> = {
  PRODUCES: "#2E9B6F",
  CONSUMES: "#C86A2E",
  SUPPLIES: "#8669C4",
  EXCLUDES: "#C44A56",
  OFFERS: "#4A7FA5",
  OPERATES: "#6E8B74",
  TRAINS: "#A3863F",
  TARGETS: "#9A5E86",
  // The ninth, and the only one no model ever wrote: a company LISTED this word as an expertise.
  // Teal because it is the gap left in the wheel — and because it must not be mistaken for [PLAIN],
  // which is what an edge is drawn in when nothing is known about it.
  RELATES: "#3E8E8C",
};
/**
 * WHICH INPUT NAMED A THING, as a shape rather than a colour.
 *
 * Colour is already spent on the relation, and a thing's source is a property of the NODE while a
 * relation is a property of the line — so the two cannot share an encoding without one of them
 * lying. Shape is what is left, and it survives the one thing colour does not: a node drawn at the
 * floor radius with eleven chords crossing it.
 *
 * ORDERED MOST PUBLIC FIRST, the same order the service resolves a node's source in: a thing named
 * by a summary is a summary thing even if an Akte names it too.
 */
export const SOURCES = ["summary", "label", "record"] as const;
export type GraphSource = (typeof SOURCES)[number];

/**
 * The polygon a thing of this source is drawn as, or null for the circle every thing used to be.
 *
 * A DIAMOND AND A TRIANGLE, not an octagon and a hexagon. Things are drawn between 4.5 and 11.5 px
 * and most sit near the floor, where a many-sided polygon IS a circle; three and four corners are
 * still three and four corners at five pixels. The multipliers put the same visual weight in each
 * mark — a diamond inscribed in the circle's radius reads as half the dot beside it.
 */
export const markPoints = (source: string, r: number): string | null => {
  if (source === "label") return `0,${-r * 1.25} ${r * 1.25},0 0,${r * 1.25} ${-r * 1.25},0`;
  if (source === "record") return `0,${-r * 1.2} ${r * 1.1},${r * 0.75} ${-r * 1.1},${r * 0.75}`;
  return null;
};

export const PLAIN = "#7F867F";
export const colourOf = (predicate: string): string => RELATION_COLOUR[predicate] ?? PLAIN;

/** Trailing legal form, dropped so a ring of labels is readable. The full name stays in the title. */
export const shortName = (name: string): string =>
  name.replace(
    / (gGmbH|GmbH & Co\. KG|GmbH|AG|SE|KG|eG|mbB|AöR|e\.V\.|mbH|Stiftung & Co\. KG|Betriebsgesellschaft mbH)$/,
    "",
  );
